import React from 'react'
import {
  FormControl,
  FormLabel,
  Text as ChakraText,
  Flex,
  VStack,
  HStack,
  Switch,
  Link,
} from '@chakra-ui/react'
import type { UrlEndpointFunctionInfo } from '../../interfaces'
import { Select } from '@chakra-ui/select'
import { Field, FieldProps, FormikProps } from 'formik'
import { useTranslation } from 'react-i18next'
import { SettingsToggleBox } from '../Settings'

interface UrlEndpointFunctionSelectorProps {
  values: any
  urlEndpointFunctions: UrlEndpointFunctionInfo[]
}

const UrlEndpointFunctionSelector = ({
  values,
  urlEndpointFunctions,
}: UrlEndpointFunctionSelectorProps): React.ReactElement => {
  const { t } = useTranslation()

  const selectedTitle = values?.urlEndpointFunctionTitle || ''
  const hasSelectedTitle = Boolean(selectedTitle)
  const isEnabled = Boolean(values?.enableUrlEndpointFunction)

  const titleList = React.useMemo(
    () =>
      (urlEndpointFunctions || []).map(item => ({
        value: item.title,
        label: item.title,
      })),
    [urlEndpointFunctions],
  )

  const versionList = React.useMemo(() => {
    if (!selectedTitle) return []
    const item = (urlEndpointFunctions || []).find(
      d => d.title === selectedTitle,
    )
    if (!item || !item?.latestPublishedVersion) return []
    return Array.from({ length: item?.latestPublishedVersion }, (_, idx) => {
      const v = idx + 1
      return { value: v, label: String(v) }
    })
  }, [urlEndpointFunctions, selectedTitle])

  return (
    <SettingsToggleBox>
      <Flex justifyContent="space-between" width="100%" alignItems="center">
        <ChakraText fontWeight="700">
          {t(
            'url_endpoint_function_mapping',
            'Enable URL endpoint function mapping',
          )}
        </ChakraText>
        <Field name="enableUrlEndpointFunction">
          {({ field, form }: FieldProps<boolean>) => (
            <Switch
              name="enableUrlEndpointFunction"
              id="enableUrlEndpointFunction"
              isChecked={field.value ?? false}
              tabIndex={-1}
              _focus={{ boxShadow: 'none', outline: 'none' }}
              onFocus={e => e.preventDefault()}
              onChange={e => {
                const checked = e.target.checked
                form.setValues({
                  ...form.values,
                  enableUrlEndpointFunction: checked,
                })
              }}
            />
          )}
        </Field>
      </Flex>
      <ChakraText mt="14px">
        {t(
          'enabled_url_function_endpoint_description',
          "Url endpoint function allows to customize asset URLs on the fly. To associate a URL endpoint function with this URL endpoint, please select the function's title and version below.",
        )}{' '}
        <Link
          isExternal
          href="https://imagekit.io/docs/url-endpoint-functions"
        >
          {t('read_more', 'Read More')}
        </Link>
      </ChakraText>
      {isEnabled ? (
        <HStack gap="2em" alignItems="center" w="100%">
          {/* First dropdown: title */}
          <Field name="urlEndpointFunctionTitle">
            {({
              field,
              form,
            }: FieldProps<string> & { form: FormikProps<any> }) => (
              <FormControl maxWidth="250px">
                <FormLabel mb={1}>
                  {t(
                    'url_endpoint_function_title',
                    'URL endpoint function title',
                  )}
                </FormLabel>
                <Select
                  maxWidth="250px"
                  value={field.value || ''}
                  placeholder={t('select_title', 'Select title')}
                  onChange={event => {
                    const newTitle = event.target.value
                    form.setValues({
                      ...form.values,
                      urlEndpointFunctionTitle: newTitle || '',
                      urlEndpointFunctionVersion: '',
                    })
                  }}
                >
                  {titleList.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          </Field>

          {/* Second dropdown: version, only enabled if title is selected */}
          <Field name="urlEndpointFunctionVersion">
            {({ field, form }: FieldProps<string | number>) => (
              <FormControl maxWidth="250px" isDisabled={!hasSelectedTitle}>
                <FormLabel mb={1}>
                  {t(
                    'url_endpoint_function_version',
                    'URL endpoint function version',
                  )}
                </FormLabel>
                <Select
                  maxWidth="250px"
                  value={field.value || ''}
                  placeholder={
                    hasSelectedTitle
                      ? t('select_version', 'Select version')
                      : t('select_title_first', 'Select title first')
                  }
                  onChange={event => {
                    form.setValues({
                      ...form.values,
                      urlEndpointFunctionVersion: Number(event.target.value),
                    })
                  }}
                  disabled={!hasSelectedTitle}
                >
                  {versionList.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          </Field>
        </HStack>
      ) : (
        <></>
      )}
    </SettingsToggleBox>
  )
}

export default UrlEndpointFunctionSelector
